import Fuse from "fuse.js";
import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { useClaims } from "../../../modules/generic/hooks/use-claims";
import { isPublishedMomentStatus } from "../../../modules/moment/published-moment-status";
import { SupabaseMomentReviewLogService } from "../../../modules/moment/server/supabase-moment-review-service";
import { useReviewerSchema } from "../../../server/auth/use-reviewer-schema";
import { useSupabaseLoader } from "../../../server/supabase/hooks";

import type { MomentStatus } from "../../../modules/moment-card/types";
import type { ReviewStatus } from "../../../modules/moment/server/supabase-moment-review-service";
import type { SupabaseHandlerArg } from "../../../server/supabase/hooks/types";
import type { ISO8601 } from "../../../utils/iso8601";
import type { UUID } from "../../../utils/uuid";

export type ReviewsVariant = "in-progress" | "previous";

export interface Review {
  moment_id: UUID;
  title: string;
  momentStatus: MomentStatus;
  reviewStatus: ReviewStatus;
  creator: string;
  requestedAt: ISO8601;
  message: string | null;
}

// The statuses a review passes through while it is still ours to act on.
const inProgressStatuses = new Set<ReviewStatus>([
  "IN_REVIEW",
  "COMMENTED",
  "APPROVED",
  "CHANGES_REQUESTED",
]);

const isPrevious = ({ momentStatus, reviewStatus }: Review): boolean =>
  isPublishedMomentStatus(momentStatus) || reviewStatus === "CANCELLED";

const isInProgress = (review: Review): boolean =>
  inProgressStatuses.has(review.reviewStatus) && !isPrevious(review);

export const isPendingReview = (review: Review): boolean =>
  isInProgress(review) &&
  (review.reviewStatus === "IN_REVIEW" || review.reviewStatus === "COMMENTED");

const newestFirst = (reviews: Review[]): Review[] =>
  reviews.toSorted((a, b) => b.requestedAt.localeCompare(a.requestedAt));

/**
 * The tabs hold different things, so they filter on different things: an in-progress review is
 * still moving through the review statuses, while a previous one is either a published
 * Communication or a review someone called off.
 */
export interface StatusFilterOption {
  value: string;
  label: string;
  field: "momentStatus" | "reviewStatus";
}

export const statusFilterOptions: Record<ReviewsVariant, StatusFilterOption[]> = {
  "in-progress": [
    { value: "IN_REVIEW", label: "Awaiting approval", field: "reviewStatus" },
    { value: "APPROVED", label: "Approved", field: "reviewStatus" },
    { value: "CHANGES_REQUESTED", label: "Changes requested", field: "reviewStatus" },
  ],
  previous: [
    { value: "active", label: "Active", field: "momentStatus" },
    { value: "finished", label: "Finished", field: "momentStatus" },
    { value: "CANCELLED", label: "Cancelled", field: "reviewStatus" },
  ],
};

export const useAssignedReviews = (
  enabled = true,
  refreshKey?: unknown,
): { data: Review[] | null; loading: boolean; error: Error | null } => {
  const personId = useClaims()?.["http://changeengine.com/person_id"];

  // A reviewer is granted none of the tables this list is drawn from, so they come in through the
  // reviewer schema instead.
  const reviewerSchema = useReviewerSchema();

  const { data, loading, error } = useSupabaseLoader<Review[], "public" | "reviewer">(
    async ({ supabase, account_id }) => {
      if (!enabled) return [];

      if (reviewerSchema) {
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        const reviewerSupabase = supabase as SupabaseHandlerArg<"reviewer">["supabase"];

        const { data: assigned, error: reviewsError } = await reviewerSupabase.rpc(
          "assigned_reviews",
          { account_id_: account_id },
        );

        if (reviewsError) throw reviewsError;

        return newestFirst(
          (assigned ?? []).map(
            ({
              moment_id,
              title,
              status: momentStatus,
              review_status,
              creator,
              requested_at,
              note,
            }) => ({
              moment_id,
              title,
              momentStatus,
              reviewStatus: review_status,
              creator,
              requestedAt: requested_at,
              message: note,
            }),
          ),
        );
      }

      if (!personId) return [];

      const { data: logs, error: logsError } = await new SupabaseMomentReviewLogService(
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        supabase as SupabaseHandlerArg<"public">["supabase"],
      ).getAllWithMoment(account_id, {
        order: [{ column: "created_at" }],
        filter: [{ key: "reviewer_person_id", operator: "eq", value: personId }],
      });

      if (logsError) throw new Error(logsError.message);

      const reached = new Map<UUID, ReviewStatus>();

      (logs ?? []).forEach(({ moment_id, review_status }) => {
        if (review_status !== "COMMENTED") reached.set(moment_id, review_status);
      });

      // A Communication collects an entry per review step. Ordered oldest first, so the first entry
      // we see for one is the request: it names the creator, when they asked and what they said.
      const reviews = new Map<UUID, Review>();

      (logs ?? []).forEach(({ moment_id, created_at, note, user_name, moment }) => {
        if (reviews.has(moment_id)) return;

        const reviewStatus = reached.get(moment_id);
        if (!moment?.title || !moment.status || !reviewStatus) return;

        reviews.set(moment_id, {
          moment_id,
          title: moment.title,
          momentStatus: moment.status,
          reviewStatus,
          creator: user_name,
          requestedAt: created_at,
          message: note,
        });
      });

      return newestFirst([...reviews.values()]);
    },
    [personId, reviewerSchema, enabled, refreshKey],
    reviewerSchema ? "reviewer" : "public",
  );

  return { data, loading, error };
};

export const useReviews = (
  variant: ReviewsVariant,
): { reviews: Review[]; loading: boolean; error: Error | null } => {
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status") ?? "";
  const q = searchParams.get("q") ?? "";

  const { data, loading, error } = useAssignedReviews();

  // The query already returns every review assigned to us, so the filters only ever narrow what we
  // have: no need to go back to the database as they change.
  const reviews = useMemo(() => {
    const inTab = (data ?? []).filter((review) =>
      variant === "previous" ? isPrevious(review) : isInProgress(review),
    );

    // Ignoring a status the other tab set
    const selected = statusFilterOptions[variant].find((option) => option.value === status);
    const byStatus = selected
      ? inTab.filter((review) => review[selected.field] === selected.value)
      : inTab;

    return q.trim()
      ? new Fuse(byStatus, { keys: ["title", "creator"] }).search(q).map(({ item }) => item)
      : byStatus;
  }, [data, variant, status, q]);

  return { reviews, loading, error };
};
